import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useJobsBoardFiltersModalContext } from '@views/jobsBoard/JobsBoardSection/JobsBoardFilters/JobsBoardFiltersModal/context';

import { FilterCollapse } from '../components/FilterCollapse';
import { StyledInput } from '../styles';

export const AgeRangeFilter = () => {
  const { modalFilters, setAgeRange } = useJobsBoardFiltersModalContext();

  const subtitle = useMemo(() => {
    if (
      ![modalFilters?.ageRange?.min, modalFilters?.ageRange?.max].some(
        Number.isInteger
      )
    ) {
      return 'Any';
    }

    return [modalFilters?.ageRange?.min, modalFilters?.ageRange?.max]
      .filter(Number.isInteger)
      .join(' - ');
  }, [modalFilters?.ageRange]);

  return (
    <FilterCollapse
      title={'Age Range'}
      subtitle={subtitle}
      content={
        <AgeRangeFilterContent
          ageRange={modalFilters?.ageRange}
          setAgeRange={setAgeRange}
        />
      }
    />
  );
};

type AgeRangeFilterContentProps = {
  ageRange: { min?: number; max?: number } | undefined;
  setAgeRange: (value: number | null | undefined, key: 'min' | 'max') => void;
};
export const AgeRangeFilterContent = ({
  ageRange,
  setAgeRange,
}: AgeRangeFilterContentProps) => {
  const minValue = useMemo(() => ageRange?.min ?? null, [ageRange?.min]);
  const maxValue = useMemo(() => ageRange?.max ?? null, [ageRange?.max]);

  const onInputChange = useCallback(
    (value: string, key: 'min' | 'max') => {
      const onlyDigits = value?.trim()?.replace(/\D/g, '') || null;
      if (!onlyDigits) {
        setAgeRange(null, key);
        return;
      }

      const onlyDigitsNumber = Number(onlyDigits);
      if (!Number.isInteger(onlyDigitsNumber)) {
        setAgeRange(null, key);
        return;
      }
      setAgeRange(onlyDigitsNumber, key);
    },
    [setAgeRange]
  );

  const [lastIndicatorKey, setLastIndicatorKey] =
    useState<'min' | 'max'>('min');

  const areInputsInvalid = useMemo(() => {
    return (
      Number.isInteger(minValue) &&
      Number.isInteger(maxValue) &&
      minValue > maxValue
    );
  }, [minValue, maxValue]);

  const isMinInputInvalid = useMemo(() => {
    return areInputsInvalid && lastIndicatorKey === 'min';
  }, [areInputsInvalid, lastIndicatorKey]);

  const isMaxInputInvalid = useMemo(() => {
    return areInputsInvalid && lastIndicatorKey === 'max';
  }, [areInputsInvalid, lastIndicatorKey]);

  return (
    <StyledContainer>
      <StyledInput
        placeholder='Min Age'
        value={minValue}
        onChange={(e) => {
          onInputChange(e?.target?.value, 'min');
          setLastIndicatorKey('min');
        }}
        status={isMinInputInvalid ? 'error' : undefined}
      />
      <div className='age-range-divider' />
      <StyledInput
        placeholder='Max Age'
        value={maxValue}
        onChange={(e) => {
          onInputChange(e?.target?.value, 'max');
          setLastIndicatorKey('max');
        }}
        status={isMaxInputInvalid ? 'error' : undefined}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  padding: 8px 0;
  display: flex;
  gap: 8px;
  align-items: center;

  .age-range-divider {
    height: 1px;
    width: 16px;
    background-color: ${({ theme }) => theme.palette.stroke[0]};
  }

  .ant-input {
    width: calc(50% - 16px);
  }
`;
